/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import axios, { type AxiosInstance } from 'axios';

import { applySessionAuthHeaders } from './request-utils';

export interface UcEnvelope<T = unknown> {
  err: string;
  errMsg?: string;
  info?: T;
}

export class UcApiError extends Error {
  err: string;

  constructor(err: string, errMsg?: string) {
    super(errMsg || '请求失败');
    this.name = 'UcApiError';
    this.err = err;
  }
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

export function getUcApiBaseUrl(): string {
  const value = import.meta.env.VITE_UC_API_BASE_URL;
  return value ? stripTrailingSlash(String(value)) : '';
}

function isTruthyEnvFlag(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return normalized !== 'false' && normalized !== '0';
}

/** UC 登录/组织能力开关：优先 VITE_UC_LOGIN_ENABLED，兼容旧 VITE_UC_API_BASE_URL */
export function isUcLoginEnabled(): boolean {
  const loginFlag = import.meta.env.VITE_UC_LOGIN_ENABLED;
  if (loginFlag !== undefined && loginFlag !== '') {
    return isTruthyEnvFlag(loginFlag);
  }
  return getUcApiBaseUrl().length > 0;
}

const ucAxios: AxiosInstance = axios.create({
  timeout: 1000 * 60 * 3,
  withCredentials: true,
});

ucAxios.interceptors.request.use(config => {
  const base = getUcApiBaseUrl();
  if (base && !config.baseURL) {
    config.baseURL = base;
  }
  applySessionAuthHeaders(config);
  return config;
});

function assertUcSuccess<T>(data: UcEnvelope<T>): T {
  if (data === null || data === undefined || typeof data !== 'object') {
    throw new UcApiError('1', '响应格式异常');
  }
  if (String(data.err) !== '0') {
    throw new UcApiError(String(data.err), data.errMsg);
  }
  return data.info as T;
}

export function ucPost<T = unknown>(
  url: string,
  body?: unknown,
  params?: Record<string, unknown>,
) {
  return ucAxios
    .post<UcEnvelope<T>>(url, body, { params })
    .then(res => assertUcSuccess(res.data));
}

export function ucGet<T = unknown>(
  url: string,
  params?: Record<string, unknown>,
) {
  return ucAxios
    .get<UcEnvelope<T>>(url, { params })
    .then(res => assertUcSuccess(res.data));
}

export { getFullRequestUrl } from './request-utils';
